import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const halftoneDotDissolveSkill: CodeoSkill = {
    name: SKILL_NAMES.halftoneDotDissolve,
    kind: 'codeo',
    description:
        'Dissolve one state into another through a grid of growing dots, like a printed halftone ' +
        'resolving — a textured scene change with a print or risograph register.',
    documentUsesSkill: check(
        (script, markup) =>
            /mask-image|maskImage/.test(`${markup}${script}`) && /radial-gradient/.test(script),
    ),
    instructions: `# Halftone Dot Dissolve

A grid of dots grows across the frame, and where the dots cover, the next state shows through. It resolves the way a printed image does — coarse, mechanical, and unmistakably *made* — which is why it suits an editorial or print-flavoured piece far better than a plain dissolve.

The dot grid is a repeating radial gradient used as a mask. Growing the gradient's dot radius is what makes the second state bleed through; nothing needs to be drawn per-dot.

## Markup

Both states are full-frame and stacked. The incoming state carries the mask.

\`\`\`html
<div id="halftone-stage" class="overflow-hidden" style="position: absolute; inset: 0;">
  <div id="print-before" class="flex items-center justify-center" style="position: absolute; inset: 0; background: var(--l5-color-1, #f4efe6); font-family: var(--l5-font-0), sans-serif; font-size: 130px; font-weight: 800; color: var(--l5-color-0, #1c1a17);">In print</div>
  <div id="print-after" class="flex items-center justify-center" style="position: absolute; inset: 0; background: var(--l5-color-2, #e8552f); font-family: var(--l5-font-0), sans-serif; font-size: 130px; font-weight: 800; color: var(--l5-color-1, #f4efe6); -webkit-mask-image: radial-gradient(circle at center, #000 0%, transparent 0%); mask-image: radial-gradient(circle at center, #000 0%, transparent 0%); -webkit-mask-size: 26px 26px; mask-size: 26px 26px; -webkit-mask-repeat: repeat; mask-repeat: repeat;">Everywhere else</div>
</div>
\`\`\`

## Timeline code

Tween the mask's gradient stops, then clear the mask. Both the \`-webkit-\` and unprefixed properties are set together so the mask survives whichever the rasterizer honours.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const afterEl = document.querySelector('#print-after');
const dots = { radius: 0 };

tl.to(dots, {
    radius: 100,
    duration: 1.2,
    ease: 'power2.inOut',
    onUpdate: () => {
        const stop = \`radial-gradient(circle at center, #000 \${dots.radius}%, transparent \${dots.radius + Math.min(6, dots.radius)}%)\`;
        afterEl.style.webkitMaskImage = stop;
        afterEl.style.maskImage = stop;
    },
}, 1.0);

tl.set(afterEl, {
    webkitMaskImage: 'none',
    maskImage: 'none',
}, '>');

window.__timelines = { main: tl };
\`\`\`

Each tile's gradient ends at its own tile edge, so neighbouring dots never overlap, and a percentage is measured to the tile's corner, not its edge. Only 100% covers the tile completely; anything less leaves a lattice of the old state, and the final clear makes the end state exact.

## Adapt

- **Dot size**: \`mask-size\` of 26px is a coarse newsprint screen. 12px reads as fine halftone; past 50px the dots become a design element in their own right and need to be composed for.
- **Directional dissolve**: give the mask a second gradient layer that slides across, so dots grow left to right rather than all at once. Reads as a wipe with texture.
- **Dither instead**: swap the radial gradient for a small repeating square pattern and the register moves from print to early computing.
- **Hold the texture**: stopping at 60% leaves a permanent halftone overlay on the new state, which is a look rather than a transition.
- **Colour separation**: two offset masked layers in different palette colours give a misregistered print feel. Offset by 3–4px, no more.
- **Reverse**: shrinking the dots dissolves *out* of a state, which suits a close.

## Pitfalls

- The mask stops must be built as a whole gradient string each update; tweening a bare number does nothing because the property is a function.
- Keep the transparent stop a few percent past the solid one, since equal stops give hard-edged dots that alias badly when rasterized — but let that soft edge shrink to nothing at radius 0 (or scale with the radius). A fixed offset paints a faint dot of the next state in every tile before the dissolve starts.
- Set both \`webkitMaskImage\` and \`maskImage\` — leaving one out makes the mask silently no-op in one renderer and not the other.
- The masked element must be full-frame, or the mask tiles against the wrong box and the dot grid drifts.
- Don't run a halftone dissolve over photographic content; the dot grid fights the image's own detail and both lose.
- This is a strong, stylized look. One per video, and only when the piece's design language already supports it — see \`${SKILL_NAMES.grainAndBloom}\` for the quieter texture option.
- Text must be fully covered or fully clear at the moment it is read. Mid-dissolve, both states' words are simultaneously legible and neither can be read.`,
};
